import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonOOPPillars = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>The 4 Pillars of OOP</h1>
                <p className="subtitle">Encapsulation, Abstraction, Inheritance, and Polymorphism: The architectural foundation of modern software.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Managing big software is hard. To keep it safe, we use 4 "Superpowers".
                        Think of a <strong>Video Game Console</strong> (like a PlayStation or Xbox).
                    </p>
                    <ul>
                        <li><strong>Encapsulation (The Shield):</strong> You cannot touch the hot computer chips inside the box. They are covered by plastic. This protects you (from shock) and the chips (from dust).</li>
                        <li><strong>Abstraction (The Controller):</strong> To play, you just press "X" or "O". You don't need to know how the signals travel through wires. The complex details are hidden behind a simple button.</li>
                        <li><strong>Inheritance (The Game Disc):</strong> "FIFA 24" and "Call of Duty" are both "Games". They both inherit the ability to "Start", "Save", and "Quit" from the parent "Game Disc".</li>
                        <li><strong>Polymorphism (The Action Button):</strong> On your controller, the "X" button does different things. In FIFA, "X" implies "Pass the Ball". In Call of Duty, "X" implies "Jump". Same Button, Different Action.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. Encapsulation: The Shield</h2>
                <p>
                    Encapsulation is about <strong>bundling data and methods</strong> and <strong>restricting access</strong> to the internal state.
                    It prevents external code from messing with delicate internals.
                </p>

                <h3>Access Modifiers in Python</h3>
                <p>
                    Unlike Java (`public`, `private`, `protected`), Python relies on <strong>naming conventions</strong>:
                </p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Syntax</th>
                            <th>Name</th>
                            <th>Meaning</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>`self.name`</td>
                            <td>Public</td>
                            <td>Accessible by everyone. (The Controller Button)</td>
                        </tr>
                        <tr>
                            <td>`self._name`</td>
                            <td>Protected</td>
                            <td>"Please don't touch this outside the class" (The Screw on the back)</td>
                        </tr>
                        <tr>
                            <td>`self.__name`</td>
                            <td>Private</td>
                            <td>Harder to touch. Python renames it internally. (The CPU inside)</td>
                        </tr>
                    </tbody>
                </table>

                <CodeBlock language="python" code={`class PaymentProcessor:
    def __init__(self):
        self.__credit_card_info = "1234-5678-9012-3456" # Private!

    def process(self):
        print(f"Charging {self.__credit_card_info}")

p = PaymentProcessor()
p.process() 
# Output: Charging 1234-5678...

# print(p.__credit_card_info) 
#  AttributeError: 'PaymentProcessor' object has no attribute '__credit_card_info'`} />

                <h3>Deep Dive: Name Mangling</h3>
                <p>
                    Did you know `__private` isn't actually private? Python just renames it to `_ClassName__private` to prevent accidental conflicts.
                    This is called <strong>Name Mangling</strong>. You can bypass it if you are naughty.
                </p>
                <CodeBlock language="python" code={`print(p._PaymentProcessor__credit_card_info)
# Output: 1234-5678... (HACKED!)`} />

                <h3>Getters, Setters, and Properties</h3>
                <p>
                    In Java, you write `getAge()` and `setAge()`. In Python, we use the `@property` decorator to make methods look like variables.
                </p>
                <CodeBlock language="python" code={`class Person:
    def __init__(self, age):
        self._age = age

    @property
    def age(self):
        """The Getter"""
        return self._age

    @age.setter
    def age(self, value):
        """The Setter (with Validation!)"""
        if value < 0:
            raise ValueError("Age cannot be negative!")
        self._age = value

p = Person(25)
p.age = 30  # Calls the setter
print(p.age) # Calls the getter

# p.age = -5 #  Raises ValueError`} />
            </div>

            <div className="content-section">
                <h2>3. Abstraction: The Interface</h2>
                <p>
                    Abstraction is about hiding the "How" and showing only the "What".
                    We use <strong>Abstract Base Classes (ABCs)</strong> to define a contract that child classes MUST follow.
                </p>

                <CodeBlock language="python" code={`from abc import ABC, abstractmethod

class Shape(ABC):
    @abstractmethod
    def area(self):
        pass

    @abstractmethod
    def perimeter(self):
        pass

# class GenericShape(Shape):
#     pass
# g = GenericShape() 
#  TypeError: Can't instantiate abstract class GenericShape with abstract methods area, perimeter

class Rectangle(Shape):
    def __init__(self, w, h):
        self.w = w
        self.h = h

    def area(self):
        return self.w * self.h

    def perimeter(self):
        return 2 * (self.w + self.h)

r = Rectangle(5, 10) # Works because we implemented the contract.`} />
            </div>

            <div className="content-section">
                <h2>4. Polymorphism: Many Forms</h2>
                <ProTip><p><strong>ELI5:</strong> "Poly" means Many. "Morph" means Form. It means "One Command, Many Shapes".</p></ProTip>
                <p>
                    Polymorphism allows different classes to be treated as instances of the same general class through a common interface.
                    The most "Pythonic" version of this is <strong>Duck Typing</strong>.
                </p>

                <ProTip><p><strong>The Duck Test:</strong> "If it looks like a duck, swims like a duck, and quacks like a duck, then it probably is a duck."</p></ProTip>

                <CodeBlock language="python" code={`class Duck:
    def quack(self):
        print("Quack!")

class Person:
    def quack(self):
        print("I'm imitating a duck!")

def make_it_speak(entity):
    # Polymorphism in action!
    # I don't care IF it is a Duck.
    # I only care if it HAS a .quack() method.
    entity.quack() 

d = Duck()
p = Person()

make_it_speak(d) # Quack!
make_it_speak(p) # I'm imitating a duck!`} />

                <h3>EAFP: Easier to Ask Forgiveness than Permission</h3>
                <p>
                    In Python, instead of checking `if isinstance(obj, Duck)`, we just try to use it and catch the error if it fails.
                </p>
                <CodeBlock language="python" code={`# LBYL (Look Before You Leap) - The Java/C++ way
if hasattr(obj, 'quack'):
    obj.quack()

# EAFP (Easier to Ask Forgiveness) - The Python way
try:
    obj.quack()
except AttributeError:
    print("This object adds silence to the conversation.")`} />
            </div>

            <div className="content-section">
                <h2>5. Deep Dive: Dynamic Attribute Access</h2>
                <p>
                    Polymorphism isn't just about methods. Python allows you to intercept attribute access dynamically using `__getattr__`.
                    This is often used in the <strong>Proxy Pattern</strong>.
                </p>
                <CodeBlock language="python" code={`class LoggerProxy:
    def __init__(self, target):
        self.target = target

    def __getattr__(self, name):
        # Intercept the call
        print(f"Log: Accessing attribute '{name}'")
        return getattr(self.target, name)

class Database:
    def connect(self):
        print("Connected to DB")

real_db = Database()
proxy_db = LoggerProxy(real_db)

# When we call proxy_db.connect(), Python doesn't find it in LoggerProxy.
# It calls __getattr__("connect"), which logs it, and then calls the real one.
proxy_db.connect()
# Output:
# Log: Accessing attribute 'connect'
# Connected to DB`} />
            </div>

            <div className="content-section">
                <h2>6. Interview FAQ</h2>
                <div className="accordion">
                    <details>
                        <summary><strong>Q1: What is the difference between Abstraction and Encapsulation?</strong></summary>
                        <p>Encapsulation is about <strong>Hiding Data</strong> (security/integrity). Abstraction is about <strong>Hiding Complexity</strong> (design/interface). You encapsulate the wires (Encapsulation) so you can press a simple button (Abstraction).</p>
                    </details>
                    <details>
                        <summary><strong>Q2: Does Python support true private variables?</strong></summary>
                        <p>No. `__name` is just name-mangled to `_ClassName__name`. A determined hacker can still access it. Python relies on developer maturity ("We are all consenting adults here").</p>
                    </details>
                    <details>
                        <summary><strong>Q3: What is Duck Typing?</strong></summary>
                        <p>A style of dynamic typing where an object's suitability is determined by the presence of certain methods/attributes, rather than its actual type inheritance.</p>
                    </details>
                    <details>
                        <summary><strong>Q4: Why use `@property` instead of getters/setters?</strong></summary>
                        <p>It allows you to start with public fields (`self.age`) and upgrade to logic later without breaking the API (`p.age` remains valid, but now calls specific logic).</p>
                    </details>
                    <details>
                        <summary><strong>Q5: What is an Abstract Base Class (ABC)?</strong></summary>
                        <p>A class that cannot be instantiated and is used to define a common interface for its subclasses. It enforces that children implement specific methods.</p>
                    </details>
                </div>
            </div>

            <div className="content-section">
                <h2>7. Extended Technical Reference</h2>

                <h3>Comparisons with Other Languages</h3>
                <p>
                    Python's approach to OOP Pillars is unique because of its philosophy: "We are all consenting adults."
                </p>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Concept</th>
                            <th>Python</th>
                            <th>Java/C++</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Encapsulation</strong></td>
                            <td>Advisory (`_`), Mangled (`__`)</td>
                            <td>Enforced (`private`, `protected`)</td>
                        </tr>
                        <tr>
                            <td><strong>Polymorphism</strong></td>
                            <td>Implicit (Duck Typing)</td>
                            <td>Explicit (Interfaces/Inheritance)</td>
                        </tr>
                        <tr>
                            <td><strong>Getters/Setters</strong></td>
                            <td>`@property` (Pythonic)</td>
                            <td>`getVariable()`, `setVariable()`</td>
                        </tr>
                    </tbody>
                </table>

                <h3>Historical Context: The Rise of `@property`</h3>
                <p>
                    Before Python 2.2, if you had a public attribute `self.x` and later wanted to add validation logic, you had to break your API and force everyone to switch to `get_x()`.
                </p>
                <p>
                    The introduction of <strong>Descriptors</strong> and the `@property` decorator solved this forever. It allows you to intercept attribute access seamlessly, meaning you should almost ALWAYS start with public attributes and only upgrade to properties if needed (YAGNI principle).
                </p>

                <h3>Glossary</h3>
                <ul className="premium-list">
                    <li><strong>Encapsulation:</strong> Bundling data and methods, hiding internal state.</li>
                    <li><strong>Abstraction:</strong> Exposing high-level interfaces, hiding low-level details.</li>
                    <li><strong>Polymorphism:</strong> Using a unified interface to operate on objects of different types.</li>
                    <li><strong>Duck Typing:</strong> "If it walks like a duck..." (Dynamic typing based on behavior).</li>
                </ul>
            </div>

        </div>
    );
};

export default PythonOOPPillars;

